import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
    applyTheme,
    getStoredPreference,
    resolveTheme,
    storeTheme,
    SYSTEM_DARK_QUERY,
    ThemeContext,
    type ThemeMode,
    type ThemePreference,
} from "./themeContext.ts";

export function ThemeProvider({ children }: { children: ReactNode }) {
    // Light on the first render, whatever is stored. A prerendered page was
    // rendered with no storage to read, so starting anywhere else would hand
    // hydration a tree that disagrees with its markup. The stored choice is
    // already on <html> by then (index.html applies it before first paint), so
    // the page itself never shows the wrong theme; only this value catches up.
    const [preference, setPreferenceState] = useState<ThemePreference>("light");
    const [theme, setThemeState] = useState<ThemeMode>("light");

    useEffect(() => {
        const stored = getStoredPreference();
        const shown = resolveTheme(stored);

        // A no-op beside the inline script, and the whole job without it.
        applyTheme(shown);
        setPreferenceState(stored);
        setThemeState(shown);
    }, []);

    // While the reader follows the system, a change there changes the page.
    useEffect(() => {
        if (preference !== "system" || typeof window.matchMedia !== "function") {
            return;
        }
        const query = window.matchMedia(SYSTEM_DARK_QUERY);
        const follow = () => {
            const shown: ThemeMode = query.matches ? "dark" : "light";
            applyTheme(shown);
            setThemeState(shown);
        };
        query.addEventListener("change", follow);
        return () => query.removeEventListener("change", follow);
    }, [preference]);

    // Applied here and not in an effect on `theme`, so the document already
    // carries the new palette when the render this triggers runs. The charts
    // read their colours off the root during render, and an effect would
    // leave them one theme behind.
    const setPreference = useCallback((nextPreference: ThemePreference) => {
        const shown = resolveTheme(nextPreference);
        applyTheme(shown);
        storeTheme(nextPreference);
        setPreferenceState(nextPreference);
        setThemeState(shown);
    }, []);

    const setTheme = useCallback(
        (nextTheme: ThemeMode) => setPreference(nextTheme),
        [setPreference],
    );

    const toggleTheme = useCallback(() => {
        setTheme(theme === "dark" ? "light" : "dark");
    }, [theme, setTheme]);

    const value = useMemo(
        () => ({ theme, preference, setTheme, setPreference, toggleTheme }),
        [theme, preference, setTheme, setPreference, toggleTheme],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
